/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
} from "react-native";
import { useNavigate, useSearchParams } from "react-router-dom";
import { functions } from "../firebase.config";
import { httpsCallable } from "firebase/functions";
import AuthHeader from "../components/AuthHeader";

const ResetBookingPin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const bookingId = searchParams.get("bookingId");

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [newPin, setNewPin] = useState("");
  const [shopInfo, setShopInfo] = useState({});
  const [storeLogo, setStoreLogo] = useState("");

  useEffect(() => {
    if (!token || !bookingId) {
      setError("Invalid reset link. Please check your email and try again.");
    }
    
    // Load shop info for header
    const loadShopInfo = async () => {
      try {
        const { db } = await import("../firebase.config");
        const { doc, getDoc } = await import("firebase/firestore");
        const settingsPath = "tenants/1234/stores/999/settings";
        
        const storeNameDoc = await getDoc(doc(db, settingsPath, "storeName"));
        const storeLogoDoc = await getDoc(doc(db, settingsPath, "storeLogo"));
        const storePhoneDoc = await getDoc(doc(db, settingsPath, "storePhone"));
        const storeEmailDoc = await getDoc(doc(db, settingsPath, "storeEmail"));
        
        setShopInfo({
          name: storeNameDoc.exists() ? storeNameDoc.data().name : "",
          phone: storePhoneDoc.exists() ? storePhoneDoc.data().phone : "",
          email: storeEmailDoc.exists() ? storeEmailDoc.data().email : "",
        });
        
        setStoreLogo(storeLogoDoc.exists() ? storeLogoDoc.data().url : "");
      } catch (error) {
        console.error("Error loading shop info:", error);
      }
    };
    
    loadShopInfo();
  }, [token, bookingId]);

  const handleResetPin = async () => {
    if (!token || !bookingId) {
      setError("Invalid reset link");
      return;
    }

    try {
      setLoading(true);
      setError("");

      console.log("🔄 Requesting PIN reset for booking:", bookingId);

      const resetBookingPin = httpsCallable(functions, "resetBookingPin");
      const result = await resetBookingPin({
        bookingId: bookingId,
        resetToken: token,
      });

      if (result.data.success) {
        setNewPin(result.data.newPin);
        setSuccess(true);
        console.log("✅ PIN reset successful");
      } else {
        setError(result.data.error || "Failed to reset PIN");
      }
    } catch (error) {
      console.error("❌ PIN reset error:", error);
      setError(
        error.message || "Failed to reset PIN. Please contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <View style={styles.container}>
        <AuthHeader 
          title="Reset PIN"
          subtitle={shopInfo.name ? `by ${shopInfo.name}` : ""}
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={false}
          shopInfo={shopInfo}
          storeLogo={storeLogo}
        />
        <View style={styles.card}>
          <Text style={styles.successIcon}>✅</Text>
          <Text style={styles.title}>PIN Reset Successful!</Text>
          <Text style={styles.subtitle}>Your new booking PIN is:</Text>
          
          <View style={styles.pinDisplay}>
            <Text style={styles.pinText}>{newPin}</Text>
          </View>

          <View style={styles.infoBox}>
            <Text style={styles.infoText}>
              ⚠️ Please save this PIN. You'll need it to access your booking.
            </Text>
            <Text style={styles.infoText}>
              This PIN has also been sent to your email for your records.
            </Text>
          </View>

          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.button}
            onPress={() => navigate(`/edit-booking/${bookingId}`)}
          >
            <Text style={styles.buttonText}>Access My Booking</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <AuthHeader 
        title="Reset PIN"
        subtitle={shopInfo.name ? `by ${shopInfo.name}` : ""}
        isDesktop={window.innerWidth >= 768}
        isAuthenticated={false}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
      />
      <View style={styles.card}>
        <Text style={styles.title}>🔐 Reset Booking PIN</Text>
        <Text style={styles.subtitle}>
          Click the button below to generate a new PIN for your booking.
        </Text>

        {error && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {!error && !loading && (
          <>
            <View style={styles.infoBox}>
              <Text style={styles.infoText}>
                A new 3-digit PIN will be generated and sent to your email.
              </Text>
            </View>

            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.button}
              onPress={handleResetPin}
              disabled={loading}
            >
              <Text style={styles.buttonText}>Generate New PIN</Text>
            </TouchableOpacity>
          </>
        )}

        {loading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.loadingText}>Resetting your PIN...</Text>
          </View>
        )}

        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={styles.backButton}
          onPress={() => navigate("/")}
        >
          <Text style={styles.backButtonText}>← Back to Home</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
    alignItems: "center",
    paddingTop: 80,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 30,
    width: "90%",
    maxWidth: 500,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  successIcon: {
    fontSize: 64,
    textAlign: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#2E3A38",
    textAlign: "center",
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    color: "#5C6B68",
    textAlign: "center",
    marginBottom: 30,
  },
  pinDisplay: {
    backgroundColor: "#E3F2EF",
    borderWidth: 3,
    borderColor: "#0E7C6B",
    borderRadius: 8,
    padding: 20,
    marginVertical: 20,
    alignItems: "center",
  },
  pinText: {
    fontSize: 48,
    fontWeight: "bold",
    color: "#0E7C6B",
    letterSpacing: 10,
  },
  infoBox: {
    backgroundColor: "#FDF3E3",
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
    padding: 15,
    borderRadius: 6,
    marginBottom: 20,
  },
  infoText: {
    fontSize: 14,
    color: "#5C6B68",
    lineHeight: 20,
    marginBottom: 8,
  },
  errorContainer: {
    backgroundColor: "#FBEAE9",
    borderLeftWidth: 4,
    borderLeftColor: "#C4453D",
    padding: 15,
    borderRadius: 6,
    marginBottom: 20,
  },
  errorText: {
    fontSize: 14,
    color: "#C4453D",
    lineHeight: 20,
  },
  loadingContainer: {
    alignItems: "center",
    marginVertical: 30,
  },
  loadingText: {
    marginTop: 15,
    fontSize: 14,
    color: "#5C6B68",
  },
  button: {
    backgroundColor: "#0E7C6B",
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
    marginBottom: 15,
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  backButton: {
    padding: 12,
    alignItems: "center",
  },
  backButtonText: {
    color: "#0E7C6B",
    fontSize: 14,
    fontWeight: "500",
  },
});

export default ResetBookingPin;

